/**
 * transactional.api.js — Stateless transactional WhatsApp send API wrapper.
 *
 * Backend endpoints (Phase 1, B1.1):
 *   POST /messages/whatsapp/send                  - one message
 *   POST /messages/whatsapp/send/bulk             - up to N in one shot
 *   GET  /messages/whatsapp/:messageId            - single message timeline
 *   POST /messages/whatsapp/status                - batch status by ids
 *   POST /messages/whatsapp/search                - filter by client_ref / date / status
 *   POST /messages/whatsapp/list                  - paginated log (Phase 2)
 *   POST /messages/whatsapp/analytics/overview    - aggregate
 *   POST /messages/whatsapp/analytics/by-template - per-template breakdown
 *
 * NOTE: All write endpoints AUTO-GENERATE Idempotency-Key if caller did not pass one.
 *       This protects against double-charging on retry.
 */

import axios from '../config/axiosConfig';

/* ────────────── helpers ────────────── */

const genIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback: timestamp + random
  return `idem_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
};

const idemHeaders = (key) => ({
  'Idempotency-Key': key || genIdempotencyKey(),
});

/* ────────────── send ────────────── */

export const sendOne = (payload, idempotencyKey) =>
  axios.post('/messages/whatsapp/send', payload, {
    headers: idemHeaders(idempotencyKey),
  });

export const sendBulk = (messages, idempotencyKey) =>
  axios.post('/messages/whatsapp/send/bulk', { messages }, {
    headers: idemHeaders(idempotencyKey),
  });

/* ────────────── retrieve ────────────── */

export const getMessage = (messageId) =>
  axios.get(`/messages/whatsapp/${encodeURIComponent(messageId)}`);

export const getMessages = (messageIds) =>
  axios.post('/messages/whatsapp/status', { message_ids: messageIds });

export const search = (filters = {}, page = 1, limit = 50) =>
  axios.post('/messages/whatsapp/search', { ...filters, page, limit });

export const list = (filters = {}, page = 1, limit = 50) =>
  axios.post('/messages/whatsapp/list', { ...filters, page, limit });

/* ────────────── analytics ────────────── */

export const analyticsOverview = (filters = {}) =>
  axios.post('/messages/whatsapp/analytics/overview', filters);

export const analyticsByTemplate = (filters = {}) =>
  axios.post('/messages/whatsapp/analytics/by-template', filters);

const transactionalApi = {
  sendOne,
  sendBulk,
  getMessage,
  getMessages,
  search,
  list,
  analyticsOverview,
  analyticsByTemplate,
};

export default transactionalApi;
